import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center gap-6">
          <div className="logo-placeholder"></div>
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="flex-1 p-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: December 10, 2025</p>
          </div>

          <Card className="bg-white border-gray-200 shadow-md p-8">
            <div className="prose prose-gray max-w-none space-y-6">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                <p className="text-gray-700">
                  Welcome to Podmixr Labs ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our podcast recording and editing platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Personal Information</h3>
                <p className="text-gray-700 mb-3">We collect information that you provide directly to us:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Account Information:</strong> Name, email address, password</li>
                  <li><strong>Payment Information:</strong> Credit card details (processed securely through Stripe)</li>
                  <li><strong>Profile Information:</strong> Display name, preferences, settings</li>
                  <li><strong>Communications:</strong> Messages you send to our support team</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.2 Content You Create</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Recordings:</strong> Audio and video podcast recordings</li>
                  <li><strong>Scripts:</strong> Teleprompter scripts you create</li>
                  <li><strong>Transcripts:</strong> Generated transcripts of your recordings</li>
                  <li><strong>Media Files:</strong> Intro clips, outro clips, and background music you upload</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">2.3 Automatically Collected Information</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Usage Data:</strong> Features you use, pages you visit, time spent on platform</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                  <li><strong>Cookies:</strong> Authentication tokens, preferences, analytics</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-700 mb-3">We use your information to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process your recordings with AI-powered enhancement tools</li>
                  <li>Process payments and manage subscriptions</li>
                  <li>Send you technical notices, updates, and support messages</li>
                  <li>Respond to your comments and questions</li>
                  <li>Detect, prevent, and address technical issues and security threats</li>
                  <li>Analyze usage patterns to improve user experience</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. AI Processing</h2>
                <p className="text-gray-700">
                  Our AI features (audio enhancement, video enhancement, transcript editing) process your content to provide the requested services. Your recordings are processed temporarily and are not used to train AI models. Pro users have access to these AI features, which are processed securely and deleted after processing is complete.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
                <p className="text-gray-700 mb-3">We may share your information with:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Service Providers:</strong> Firebase (authentication), Stripe (payments), cloud storage providers</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  <strong>We do NOT:</strong> Sell your personal information to third parties or use your recordings for advertising purposes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
                <p className="text-gray-700">
                  We implement appropriate technical and organizational measures to protect your information, including encryption, secure servers, and access controls. However, no method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
                <p className="text-gray-700 mb-3">You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Access:</strong> Request a copy of your personal information</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                  <li><strong>Export:</strong> Download your recordings and content</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  To exercise these rights, contact us at support@podmixrlabs.com
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
                <p className="text-gray-700">
                  We retain your information for as long as your account is active or as needed to provide services. When you delete your account, we will delete your personal information within 30 days, except where retention is required by law or for legitimate business purposes (e.g., payment records).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
                <p className="text-gray-700">
                  Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. International Users</h2>
                <p className="text-gray-700">
                  Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in compliance with applicable data protection laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Policy</h2>
                <p className="text-gray-700">
                  We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a notice on our platform. Your continued use of the service after changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
                <p className="text-gray-700">
                  If you have questions about this Privacy Policy, please contact us:
                </p>
                <ul className="list-none space-y-2 text-gray-700 mt-3">
                  <li><strong>Email:</strong> support@podmixrlabs.com</li>
                  <li><strong>Company:</strong> Podmixr Labs</li>
                </ul>
              </section>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-8">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> This is a general template. For complete legal compliance, we recommend having this reviewed by a legal professional familiar with data protection laws in your jurisdiction (GDPR, CCPA, etc.).
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
